import React from 'react';
import { EmployeeRecord } from '../../../utils/employee-indexeddb';
import { DEPARTMENT_FILTER_PREFIX, HEADQUARTER_FILTER_PREFIX, parseDepartmentHierarchy } from '../../../utils/department-utils';

interface MappingTableProps {
  filteredEmployees: EmployeeRecord[];
  availableApiKeys: Array<{ id: string; name: string }>;
  selectedApiKey: { [email: string]: string };
  searchTerm: string;
  headquarterSelectValue: string;
  activeHeadquarter: string;
  departmentSelectValue: string;
  isDepartmentSelectionDisabled: boolean;
  headquarterOptions: string[];
  departmentOptions: Map<string, string[]>;
  selectedRole: string;
  showOnlyMapped: boolean;
  onSearchTermChange: (term: string) => void;
  onHeadquarterChange: (value: string) => void;
  onDepartmentChange: (value: string) => void;
  onSelectedRoleChange: (role: string) => void;
  onShowOnlyMappedChange: (show: boolean) => void;
  onSelectedApiKeyChange: React.Dispatch<React.SetStateAction<{ [email: string]: string }>>;
  onManualMap: (employee: EmployeeRecord, apiKeyId: string, apiKeyName: string) => void;
  onDeleteEmployee: (email: string) => void;
}

export const MappingTable: React.FC<MappingTableProps> = ({
  filteredEmployees,
  availableApiKeys,
  selectedApiKey,
  searchTerm,
  headquarterSelectValue,
  activeHeadquarter,
  departmentSelectValue,
  isDepartmentSelectionDisabled,
  headquarterOptions,
  departmentOptions,
  selectedRole,
  showOnlyMapped,
  onSearchTermChange,
  onHeadquarterChange,
  onDepartmentChange,
  onSelectedRoleChange,
  onShowOnlyMappedChange,
  onSelectedApiKeyChange,
  onManualMap,
  onDeleteEmployee,
}) => {
  return (
    <>
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
            className="flex-1 min-w-[120px] sm:min-w-[200px] px-2 sm:px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <select
            value={headquarterSelectValue}
            onChange={(e) => onHeadquarterChange(e.target.value)}
            className="px-2 sm:px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="all">All Headquarters</option>
            {headquarterOptions.map(hq => (
              <option key={hq} value={`${HEADQUARTER_FILTER_PREFIX}${hq}`}>
                {hq}
              </option>
            ))}
          </select>

          <select
            value={departmentSelectValue}
            onChange={(e) => onDepartmentChange(e.target.value)}
            disabled={isDepartmentSelectionDisabled}
            className="px-2 sm:px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value={activeHeadquarter ? `${HEADQUARTER_FILTER_PREFIX}${activeHeadquarter}` : 'all'}>
              {activeHeadquarter ? 'All Departments' : 'Select headquarter first'}
            </option>
            {activeHeadquarter && departmentOptions.get(activeHeadquarter)?.map(dept => (
              <option
                key={dept}
                value={`${DEPARTMENT_FILTER_PREFIX}${activeHeadquarter}::${dept}`}
              >
                {dept}
              </option>
            ))}
          </select>

          <input
            type="text"
            value={selectedRole}
            onChange={(e) => onSelectedRoleChange(e.target.value)}
            placeholder="Filter by role (regex supported)"
            title="Filter by role (regex supported)"
            className="px-2 sm:px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />

          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyMapped}
              onChange={(e) => onShowOnlyMappedChange(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Only Mapped</span>
          </label>
        </div>
      </div>

      {/* Employee Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Employee ID
                </th>
                <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="hidden md:table-cell px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="hidden lg:table-cell px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Headquarter
                </th>
                <th className="hidden lg:table-cell px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Department
                </th>
                <th className="hidden md:table-cell px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  API Key
                </th>
                <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredEmployees.map((employee, idx) => {
                const parsed = parseDepartmentHierarchy(employee.department);
                const headquarter = employee.headquarter || parsed.headquarter;
                const departmentUnit = employee.departmentUnit || parsed.department;
                const departmentLabel = departmentUnit || employee.normalizedDepartment || employee.department || '-';

                return (
                  <tr key={employee.email} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'}>
                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {employee.employeeId || '-'}
                    </td>
                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {employee.name || '-'}
                    </td>
                    <td className="hidden md:table-cell px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {employee.email}
                    </td>
                    <td className="hidden lg:table-cell px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {headquarter || '-'}
                    </td>
                    <td className="hidden lg:table-cell px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {departmentLabel}
                    </td>
                    <td className="hidden md:table-cell px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {employee.role || '-'}
                    </td>
                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {employee.apiKeyId ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          {employee.apiKeyName}
                        </span>
                      ) : (
                        <span className="text-gray-400">Not mapped</span>
                      )}
                    </td>
                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-2">
                        {!employee.apiKeyId && availableApiKeys.length > 0 && (
                          <div className="flex items-center space-x-1">
                            <select
                              value={selectedApiKey[employee.email] || ''}
                              onChange={(e) => onSelectedApiKeyChange(prev => ({ ...prev, [employee.email]: e.target.value }))}
                              className="text-sm px-2 py-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded"
                              title="Select API Key to map"
                            >
                              <option value="">Select API Key...</option>
                              {availableApiKeys.map(key => (
                                <option key={key.id} value={key.id}>
                                  {key.name}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => {
                                const apiKeyId = selectedApiKey[employee.email];
                                const apiKey = availableApiKeys.find(k => k.id === apiKeyId);
                                if (apiKey) {
                                  onManualMap(employee, apiKey.id, apiKey.name);
                                  onSelectedApiKeyChange(prev => {
                                    const newState = { ...prev };
                                    delete newState[employee.email];
                                    return newState;
                                  });
                                }
                              }}
                              disabled={!selectedApiKey[employee.email]}
                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Map
                            </button>
                          </div>
                        )}
                        {employee.apiKeyId && (
                          <button
                            onClick={() => onManualMap(employee, '', '')}
                            className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200"
                            title="Unmap"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => onDeleteEmployee(employee.email)}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                          title="Delete"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredEmployees.length === 0 && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No employees found</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Get started by importing a CSV file with employee information.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
